import { Component, ChangeDetectionStrategy, ViewEncapsulation } from '@angular/core';
import { ICellRendererAngularComp } from '@ag-grid-community/angular';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { NgFor, NgIf } from '@angular/common';
import { ICellRendererParams } from '@ag-grid-community/core';
import { MatFormFieldModule }
	from '@angular/material/form-field';
import { MatOptionModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';

import { TranslationService } from '../../../../../shared/services/translation.service';
import { PersonMember } from '../../../../../shared/models/user-info';
import { OrganizationUserInfo } from '../../../../models/organization-user-info';

interface CellParams extends ICellRendererParams<unknown, unknown> {
	isEditMode: boolean;
	isSupplierOrganization?: boolean;
	organizationCardUsersList: OrganizationUserInfo[];
	onRoleChangeCallback: (updatedPersonMembers: OrganizationUserInfo[] | PersonMember[], newRole: string) => void;
}

@Component({
	selector: 'copo-manage-user-role',
	templateUrl: './manage-user-role.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		MatSelectModule,
		MatFormFieldModule,
		NgIf,
		NgFor,
		FormsModule,
		MatOptionModule,
		MatButtonModule,
		ReactiveFormsModule,
	]
})
export class ManageUserRoleComponent implements ICellRendererAngularComp {
	params!: CellParams;

	selectedRole!: string;
	organizationUser!: OrganizationUserInfo;
	editMode = false;
	roleOptions: { value: string; viewValue: string }[];

	roleForm!: FormGroup;
	updatedOrganizationUsersPM: OrganizationUserInfo[] = [];

	constructor(
		private readonly translationService: TranslationService,
	) {
		this.roleOptions = [
			{ value: 'dsu', viewValue: this.translationService.getTR('organizationrole.dsu') },
			{ value: 'user', viewValue: this.translationService.getTR('organizationrole.user') },
			{ value: 'subscription', viewValue: this.translationService.getTR('organizationrole.subscription') }
		];
		this.roleForm = new FormGroup({
			roleFormControl: new FormControl('')
		});
	}

	refresh(params: CellParams): boolean {
		this.agInit(params);
		return true;
	}

	agInit(params: CellParams): void {
		this.params = params;
		this.roleOptions = this.buildRoleOptions(Boolean(this.params.isSupplierOrganization));

		if (this.params.organizationCardUsersList.length !== 0) {
			this.organizationUser = <OrganizationUserInfo> this.params.data;
			// Find the correct object in organizationCardUsersList that matches the condition
			const selectedUser = this.params.organizationCardUsersList.find(user =>
				// Match user by id or another unique attribute
				user.id === this.organizationUser.id);

			// If a matching user is found, set the selectedRole using the role code, otherwise use a default role
			if (selectedUser) {
				const roleCode = selectedUser.personMembers.role;
				this.selectedRole = roleCode;
				if (this.params.isEditMode
					&& (selectedUser.personMembers.status === 'activ'
						|| selectedUser.personMembers.status === 'Activ')
					&& (selectedUser.status === 'activ' || selectedUser.status === 'Activ')) {
					this.roleForm.get('roleFormControl')?.setValue(this.selectedRole); // Raw value for the dropdown
					this.editMode = this.params.isEditMode;
				}
			}
		} else {
			// If the list is empty, fallback to params.data.role that is for userlist
			this.selectedRole = <string> this.params.value;
		}
	}

	onRoleChange(event: MatSelectChange, rowData: unknown): void {
		if (this.params.organizationCardUsersList.length !== 0) {
			this.selectedRole = <string> event.value; // Update to the new raw value
			// Clone the current rowData so we don't modify the original object
			const clonedOrgUser = <OrganizationUserInfo> rowData;

			// Add this updated person to the temp updatedPersonMembers list for saving later
			const index = this.updatedOrganizationUsersPM.findIndex(user => user.id === clonedOrgUser.id);
			if (index !== -1) {
				// If the user already exists in the list, update it
				this.updatedOrganizationUsersPM[index] = clonedOrgUser;
			} else {
				// Otherwise, add the updated person to the list
				this.updatedOrganizationUsersPM.push(clonedOrgUser);
			}

			this.params.onRoleChangeCallback(this.updatedOrganizationUsersPM, this.selectedRole);
		}
	}

	getDisplayRole(role: string): string {
		const roleOption = this.roleOptions.find(option => option.value === role);
		return roleOption ? roleOption.viewValue
			: this.translationService.getTR(`organizationrole.${ role }`);
	}

	isRoleInOptions(role: string): boolean {
		return this.roleOptions.some(option => option.value === role);
	}

	private buildRoleOptions(isSupplierOrganization: boolean): { value: string; viewValue: string }[] {
		const options = [
			{ value: 'dsu', viewValue: this.translationService.getTR('organizationrole.dsu') },
			{ value: 'user', viewValue: this.translationService.getTR('organizationrole.user') },
			{ value: 'subscription', viewValue: this.translationService.getTR('organizationrole.subscription') }
		];

		if (isSupplierOrganization) {
			options.push({
				value: 'hlpdk',
				viewValue: this.translationService.getTR('organizationrole.hlpdk')
			});
		}

		return options;
	}
}
